/**
 * Response body normalization — ensures binary response bodies are
 * base64-encoded for transport over WebSocket/postMessage.
 *
 * After normalization:
 *   Blob           → base64 string + bodyEncoding: 'base64'
 *   ArrayBuffer    → base64 string + bodyEncoding: 'base64'
 *   TypedArray     → base64 string + bodyEncoding: 'base64'
 *   string/object  → unchanged (pass-through)
 *
 * The receiving end (service worker, discovery server proxy) already
 * knows how to decode bodyEncoding: 'base64' back to binary.
 */

/**
 * Normalize a route handler response body for transport.
 * Binary bodies (Blob, ArrayBuffer, TypedArray) are base64-encoded
 * with bodyEncoding: 'base64' set automatically.
 * String/object bodies pass through unchanged.
 *
 * With { keepBinary: true } binary bodies pass through untouched — used when
 * the transport can ship raw bytes (WS binary frames, negotiated via the
 * server's `binaryFrames` welcome advert; binary-transfer-performance #3).
 * The transport layer (sendProxiedResponse) makes the final framing call.
 *
 * @param {object} response - Route handler return value ({ status, headers, body, ... })
 * @param {object} [options] - { keepBinary }
 * @returns {Promise<object>} - Response with body encoded for transport
 */
export async function normalizeResponse(response, { keepBinary = false } = {}) {
    if (!response || response.body == null) return response

    // Already has explicit bodyEncoding — respect it
    if (response.bodyEncoding) return response

    const body = response.body

    if (
        keepBinary &&
        ((typeof Blob !== 'undefined' && body instanceof Blob) ||
            body instanceof ArrayBuffer ||
            ArrayBuffer.isView(body))
    ) {
        return response
    }

    // Blob → base64
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
        const arrayBuffer = await body.arrayBuffer()
        return { ...response, body: arrayBufferToBase64(arrayBuffer), bodyEncoding: 'base64' }
    }

    // ArrayBuffer → base64
    if (body instanceof ArrayBuffer) {
        return { ...response, body: arrayBufferToBase64(body), bodyEncoding: 'base64' }
    }

    // TypedArray (Uint8Array, Int8Array, etc.) → base64
    if (ArrayBuffer.isView(body)) {
        const buffer = body.buffer.slice(body.byteOffset, body.byteOffset + body.byteLength)
        return { ...response, body: arrayBufferToBase64(buffer), bodyEncoding: 'base64' }
    }

    // String, object, etc. — pass through
    return response
}

/**
 * Convert an ArrayBuffer to a base64 string.
 * Uses chunked String.fromCharCode to avoid stack overflow on large payloads.
 */
function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer)
    const chunkSize = 8192
    let binaryString = ''
    for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize)
        binaryString += String.fromCharCode(...chunk)
    }
    return btoa(binaryString)
}